import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PreferencesContext,
  type Preferences,
  type PreferencesValue,
  type TablePrefs,
  type Theme,
} from './preferences-store.js';
import type { ReactNode } from 'react';
import { apiRequest } from '../lib/api-client';
import { useAuth } from '../auth/auth-store';
import { setLocale } from '../lib/i18n';
import { applyFormatSettings, type DateFormat, type TimeFormat } from '../ui/format';
import { DEFAULT_RAIL_THEME, isRailTheme } from './nav/themes';

/**
 * Interface preferences, stored on the account.
 *
 * They follow the person rather than the browser: somebody who collapses the rail on a laptop should
 * find it collapsed on a desktop, and clearing site data should not reset how the product looks.
 *
 * `localStorage` is still written, as a cache. Without it the rail renders in its default shape for
 * the moment between the page loading and `/v1/auth/me` answering, and the visible jump is worse than
 * the duplication. The server is the source of truth; the cache only decides the first paint.
 */


const DEFAULTS: Preferences = {
  railCollapsed: false,
  railTheme: DEFAULT_RAIL_THEME,
  railFlyout: true,
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h',
  theme: 'system',
  toasts: true,
  sounds: true,
  tables: {},
};

const THEMES: Theme[] = ['system', 'light', 'dark'];

const DATE_FORMATS: DateFormat[] = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
const TIME_FORMATS: TimeFormat[] = ['24h', '12h'];

const CACHE_KEY = 'crm.preferences';



function readCache(): Partial<Preferences> {
  try {
    const raw = localStorage.getItem(CACHE_KEY);

    return raw ? (JSON.parse(raw) as Partial<Preferences>) : {};
  } catch {
    // A private window, blocked site data, or something else wrote nonsense there.
    return {};
  }
}

function writeCache(preferences: Preferences): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(preferences));
  } catch {
    // Not being able to remember the first paint is not a reason to fail the page.
  }
}

/** Only the keys this build knows, and only values it can use. */
function reconcile(stored: Record<string, unknown> | undefined): Partial<Preferences> {
  if (!stored) return {};

  const result: Partial<Preferences> = {};

  if (typeof stored.railCollapsed === 'boolean') result.railCollapsed = stored.railCollapsed;
  if (typeof stored.railFlyout === 'boolean') result.railFlyout = stored.railFlyout;
  if (isRailTheme(stored.railTheme)) result.railTheme = stored.railTheme;

  if (THEMES.includes(stored.theme as Theme)) result.theme = stored.theme as Theme;
  if (typeof stored.toasts === 'boolean') result.toasts = stored.toasts;
  if (typeof stored.sounds === 'boolean') result.sounds = stored.sounds;
  if (stored.tables && typeof stored.tables === 'object' && !Array.isArray(stored.tables)) {
    const tables: Record<string, TablePrefs> = {};

    for (const [key, value] of Object.entries(stored.tables as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const prefs = value as Record<string, unknown>;
      tables[key] = {
        ...(Array.isArray(prefs.columns)
          ? { columns: prefs.columns.filter((c): c is string => typeof c === 'string') }
          : {}),
        ...(typeof prefs.pageSize === 'number' ? { pageSize: prefs.pageSize } : {}),
      };
    }

    result.tables = tables;
  }

  if (DATE_FORMATS.includes(stored.dateFormat as DateFormat)) {
    result.dateFormat = stored.dateFormat as DateFormat;
  }

  if (TIME_FORMATS.includes(stored.timeFormat as TimeFormat)) {
    result.timeFormat = stored.timeFormat as TimeFormat;
  }

  return result;
}

export function PreferencesProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { user, status } = useAuth();

  const [preferences, setPreferences] = useState<Preferences>(() => ({
    ...DEFAULTS,
    ...reconcile(readCache() as Record<string, unknown>),
  }));

  const [saving, setSaving] = useState(false);

  // What the server last confirmed, so an in-flight save is not undone by the profile arriving.
  const pending = useRef<Partial<Preferences>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The account's preferences win over the cache the moment they arrive — except for anything the
  // person changed in the last second, which has not reached the server yet and must not be undone
  // by the profile that was already in flight when they changed it.
  useEffect(() => {
    if (status !== 'authenticated' || !user) return;

    setPreferences({ ...DEFAULTS, ...reconcile(user.preferences), ...pending.current });
  }, [status, user]);

  const flush = useCallback(async () => {
    const changes = pending.current;

    if (Object.keys(changes).length === 0) return;

    pending.current = {};
    setSaving(true);

    try {
      await apiRequest('/v1/auth/preferences', { method: 'PATCH', body: changes });
    } catch {
      // A preference that failed to save is not worth interrupting anybody over; the local value
      // stands and the next change tries again.
    } finally {
      setSaving(false);
    }
  }, []);

  const set = useCallback<PreferencesValue['set']>(
    (key, value) => {
      setPreferences((current) => {
        const next = { ...current, [key]: value };

        writeCache(next);

        return next;
      });

      pending.current = { ...pending.current, [key]: value };

      // Debounced: dragging a colour through six swatches is one request, not six.
      clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), 600);
    },
    [flush],
  );

  // A change made just before the tab closes should still reach the server.
  useEffect(() => {
    function onHide(): void {
      if (document.visibilityState === 'hidden') void flush();
    }

    document.addEventListener('visibilitychange', onHide);

    return () => {
      document.removeEventListener('visibilitychange', onHide);
      clearTimeout(timer.current);
    };
  }, [flush]);

  /*
   * The one place the account's regional settings reach the code that renders dates.
   *
   * `format.ts` keeps them as module state rather than as context, because it exports plain
   * functions that forty screens call without thinking about them. This is what keeps that state in
   * step with the account: it runs when the profile arrives and whenever any of the four settings
   * changes, and every render after it uses the new ones.
   */
  useEffect(() => {
    applyFormatSettings({
      ...(user?.locale ? { locale: user.locale } : {}),
      // Undefined, not the browser's zone: `format.ts` falling back to the browser is the honest
      // answer for somebody who has not chosen, and naming the browser's zone here would turn a
      // non-choice into one.
      timeZone: user?.timezone,
      dateFormat: preferences.dateFormat,
      timeFormat: preferences.timeFormat,
    });

    if (user?.locale) setLocale(user.locale as Parameters<typeof setLocale>[0]);
  }, [user?.locale, user?.timezone, preferences.dateFormat, preferences.timeFormat]);

  // The theme is an attribute on the root: the stylesheet keys its dark palette off it, and
  // "system" removes the attribute so the media query decides.
  useEffect(() => {
    if (preferences.theme === 'system') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = preferences.theme;
  }, [preferences.theme]);

  const value = useMemo(() => ({ preferences, set, saving }), [preferences, set, saving]);

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}


